import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlassCard from '../components/GlassCard';
import FuturisticButton from '../components/FuturisticButton';

const { FiUpload, FiKey, FiZap, FiEye, FiArrowRight } = FiIcons;

const HowItWorks = () => {
  const steps = [
    {
      step: 1,
      icon: FiKey,
      title: 'Enter Your OpenAI API Key',
      description: 'Securely provide your OpenAI API key. This key is only used during your session and never stored.',
      details: [
        'Your API key remains completely private',
        'Used only for your current session',
        'Automatically cleared when you leave'
      ]
    },
    {
      step: 2,
      icon: FiUpload,
      title: 'Upload Your Medical Report',
      description: 'Upload your medical report in PDF, image (JPEG/PNG), or text format.',
      details: [
        'Supports multiple file formats',
        'Secure file processing',
        'No files stored on our servers'
      ]
    },
    {
      step: 3,
      icon: FiZap,
      title: 'AI Analysis & Processing',
      description: 'Our AI extracts and analyzes the medical content using advanced language models.',
      details: [
        'OCR technology for image files',
        'Smart text extraction from PDFs',
        'Context-aware medical analysis'
      ]
    },
    {
      step: 4,
      icon: FiEye,
      title: 'Get Clear Explanations',
      description: 'Receive easy-to-understand explanations of your medical report in plain language.',
      details: [
        'Complex terms simplified',
        'Key findings highlighted',
        'Actionable insights provided'
      ]
    }
  ];

  const supportedFormats = [
    {
      format: 'PDF',
      description: 'Laboratory reports, imaging results, discharge summaries'
    },
    {
      format: 'JPEG/PNG',
      description: 'Photos of printed reports, handwritten notes'
    },
    {
      format: 'Text',
      description: 'Copy-pasted medical text, digital reports'
    }
  ];

  return (
    <div className="min-h-screen pt-20 sm:pt-24 md:pt-28 lg:pt-32 relative">
      {/* Hero Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 terminal-text px-2">
              How MedExplainGPT Works
            </h1>
            <p className="text-lg sm:text-xl text-green-200 mb-6 sm:mb-8 px-2">
              Four simple steps to understand your medical reports clearly and securely
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-12 sm:space-y-16">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } items-center gap-8 lg:gap-12`}
              >
                {/* Content */}
                <div className="flex-1 w-full">
                  <GlassCard className="p-6 sm:p-8">
                    <div className="flex items-center mb-4 sm:mb-6">
                      <div className="bg-gradient-to-r from-green-600 to-green-500 text-black w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-base sm:text-lg mr-3 sm:mr-4">
                        {step.step}
                      </div>
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
                        {step.title}
                      </h2>
                    </div>
                    <p className="text-base sm:text-lg text-green-200 mb-4 sm:mb-6">
                      {step.description}
                    </p>
                    <ul className="space-y-2 sm:space-y-3">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center text-sm sm:text-base text-white">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-3 flex-shrink-0"></div>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </GlassCard>
                </div>

                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="bg-gradient-to-r from-green-400/20 to-green-500/20 w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-2xl flex items-center justify-center border border-green-400/30">
                    <SafeIcon icon={step.icon} className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 text-green-400" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Formats */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 terminal-text">
              Supported File Formats
            </h2>
            <p className="text-lg sm:text-xl text-green-200">
              Upload your medical reports in any of these formats
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {supportedFormats.map((format, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <GlassCard className="p-4 sm:p-6 text-center">
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">
                    {format.format}
                  </h3>
                  <p className="text-sm sm:text-base text-green-200">
                    {format.description}
                  </p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Notice */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <GlassCard className="p-6 sm:p-8" glow>
              <div className="text-center">
                <SafeIcon icon={FiKey} className="h-10 w-10 sm:h-12 sm:w-12 text-green-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                  Your Privacy is Our Priority
                </h3>
                <p className="text-base sm:text-lg text-green-200 mb-4 sm:mb-6">
                  We use a zero-storage approach. Your API key and medical reports are processed in real-time and never stored on our servers. Your data remains completely private.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 text-sm">
                  <GlassCard className="p-3 sm:p-4">
                    <strong className="text-green-400">No Data Storage</strong>
                    <p className="text-white mt-1">Files are processed and immediately discarded</p>
                  </GlassCard>
                  <GlassCard className="p-3 sm:p-4">
                    <strong className="text-green-400">Session-Only Keys</strong>
                    <p className="text-white mt-1">API keys are cleared when you leave</p>
                  </GlassCard>
                  <GlassCard className="p-3 sm:p-4">
                    <strong className="text-green-400">Secure Processing</strong>
                    <p className="text-white mt-1">All connections use HTTPS encryption</p>
                  </GlassCard>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <GlassCard className="p-6 sm:p-8" glow>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 terminal-text">
                Ready to Get Started?
              </h2>
              <p className="text-lg sm:text-xl text-green-200 mb-6 sm:mb-8">
                Upload your first medical report and see how easy it is to understand
              </p>
              <div className="max-w-xs mx-auto">
                <FuturisticButton as={Link} to="/upload" size="lg" icon={FiArrowRight}>
                  <span className="hidden sm:inline">Start Now</span>
                  <span className="sm:hidden">Start</span>
                </FuturisticButton>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;